import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Page } from '../models/page-wrapper.model';
import { Settlement, SettlementSelloutStatus } from './settlement.model';
import { SettlementAttachment } from './settlement-attachment.model';

@Injectable({
  providedIn: 'root'
})
export class SettlementService {

  private apiUrl = `${environment.restApiUrl}settlements`;

  constructor(private http: HttpClient) { }

  private buildSearchParams(searchParams: any): HttpParams {
    let params = new HttpParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== null && item !== undefined) {
              params = params.append(key, String(item));
            }
          });
        } else {
          params = params.append(key, String(value));
        }
      }
    });
    return params;
  }

  searchSettlements(searchParams: any): Observable<Page<Settlement>> {
    const params = this.buildSearchParams(searchParams);
    return this.http.get<Page<Settlement>>(this.apiUrl, { params });
  }

  /**
   * Gets a single settlement by its ID.
   * @param id The ID of the settlement.
   */
  getSettlementById(id: number): Observable<Settlement> {
    return this.http.get<Settlement>(`${this.apiUrl}/${id}`);
  }

  getSelloutStatuses(id: number): Observable<SettlementSelloutStatus[]> {
    return this.http.get<SettlementSelloutStatus[]>(`${this.apiUrl}/${id}/sellout-statuses`);
  }

  /**
   * Creates a new settlement.
   * @param settlement The settlement data to create.
   */
  createSettlement(settlement: Partial<Settlement>): Observable<Settlement> {
    return this.http.post<Settlement>(this.apiUrl, settlement);
  }

  /**
   * Updates an existing settlement.
   * @param id The ID of the settlement to update.
   * @param settlement The updated settlement data.
   */
  updateSettlement(id: number, settlement: Partial<Settlement>): Observable<Settlement> {
    return this.http.put<Settlement>(`${this.apiUrl}/${id}`, settlement);
  }

  /**
   * Deletes a settlement by its ID.
   * @param id The ID of the settlement to delete.
   */
  deleteSettlement(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Updates the status of a settlement.
   * @param id The ID of the settlement.
   * @param workflowStatusId The ID of the new workflow status.
   * @param updatedByUserId The ID of the user performing the update.
   */
  updateSettlementStatus(id: number, workflowStatusId: number, updatedByUserId: number): Observable<Settlement> {
    const params = new HttpParams()
      .set('workflowStatusId', workflowStatusId.toString())
      .set('updatedByUserId', updatedByUserId.toString());
    return this.http.patch<Settlement>(`${this.apiUrl}/${id}/status`, null, { params });
  }

  // --- Attachment Methods ---

  uploadAttachments(settlementId: number, attachments: SettlementAttachment[]): Observable<SettlementAttachment[]> {
    return this.http.post<SettlementAttachment[]>(`${this.apiUrl}/${settlementId}/attachments`, attachments);
  }

  getAttachmentViewUrl(settlementId: number, attachmentId: number): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.apiUrl}/${settlementId}/attachments/${attachmentId}/view-url`);
  }

  /**
   * Updates the A&P values for a specific settlement.
   * @param id The ID of the settlement to update.
   * @param settlement The A&P values to update.
   */
  updateApValues(id: number, settlement: Partial<Settlement>): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}/${id}/update-ap-values`, settlement);
  }

  /**
   * Updates the AR values for a specific settlement.
   * @param id The ID of the settlement to update.
   * @param settlement The AR values to update.
   */
  updateArValues(id: number, settlement: Partial<Settlement>): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}/${id}/update-ar-values`, settlement);
  }
}
